/**
 * Cart eligibility helpers (invoice payment, card failure fallback).
 * Pure logic; no API calls.
 */
import { INVOICE_FALLBACK_DECLINE_CODES, INVOICE_FALLBACK_ERROR_CODES, PAYMENT_METHODS } from '../../checkout/constants/payment.constants';
import { CartView } from '../types/cartTypes';

/**
 * Returns true when the event date is within (or past) the invoice cutoff window.
 * This is a hard block — invoice must NEVER be offered when this returns true,
 * even as a card-payment-failure fallback, because there is not enough time
 * to collect payment before the event.
 */
export interface EventConfig {
  paymentMethodsConfig?: string;
  invoiceMinThreshold?: { centAmount: number };
  invoiceCutoffDays?: number;
  startDate?: string;
}

export const isEventWithinInvoiceCutoff = (eventConfig: EventConfig) => {
  if (!eventConfig) return false;
  const cutoffDays = eventConfig.invoiceCutoffDays || 0;
  if (cutoffDays <= 0 || !eventConfig.startDate) return false;

  const eventDate = new Date(eventConfig.startDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eventDate.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntilEvent = Math.floor((eventDate.getTime() - today.getTime()) / msPerDay);

  return daysUntilEvent <= cutoffDays;
};

/**
 * Check if invoice payment is available based on event configuration.
 * Enforces: cardAndInvoice config, minimum cart amount, and event date cutoff.
 */
export const isInvoicePaymentAvailable = (cart: CartView | null, eventConfig: EventConfig) => {
  if (!cart || !cart.totalPrice) return false;

  if (eventConfig.paymentMethodsConfig !== PAYMENT_METHODS.CARD_AND_INVOICE) {
    return false;
  }

  const thresholdCentAmount = eventConfig.invoiceMinThreshold?.centAmount || 0;

  let cartTotalCentAmount = 0;
  if (typeof cart.totalPrice === 'number') {
    cartTotalCentAmount = cart.totalPrice;
  } else if (cart.totalPrice && typeof cart.totalPrice === 'object' && 'centAmount' in cart.totalPrice) {
    cartTotalCentAmount = (cart.totalPrice as { centAmount: number }).centAmount;
  }

  if (typeof cartTotalCentAmount !== 'number' || isNaN(cartTotalCentAmount)) {
    return false;
  }
  if (cartTotalCentAmount <= thresholdCentAmount) {
    return false;
  }

  // Reuse the shared cutoff helper — single source of truth for date logic.
  if (isEventWithinInvoiceCutoff(eventConfig)) {
    return false;
  }

  return true;
};

export const isCardPaymentFailureEligibleForInvoice = (errorCode: string, declineCode: string) => {
  const errorCodeMatch = INVOICE_FALLBACK_ERROR_CODES.includes(errorCode);
  const declineCodeMatch = INVOICE_FALLBACK_DECLINE_CODES.includes(declineCode);
  return errorCodeMatch || declineCodeMatch;
};
